import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import useSencoPupils from '../../hooks/useSencoPupils';
import useMyReferralDocuments from '../../hooks/useMyReferralDocuments';
import { getOrgConfig } from '../../utils/orgResolution';
import { buildFamilyChild, familyUserName } from '../../utils/familyPortal';
import { DOCUMENTS_LOAD_FAILED, addedNotice } from '../../utils/referralDocumentPortal';
import HarbourDocumentForm from '../../components/harbour/HarbourDocumentForm';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import FamilyChildCard from './FamilyChildCard';
import FamilyLoadError from './FamilyLoadError';
import FamilyLoadingState from './FamilyLoadingState';
import FamilyPortalShell from './FamilyPortalShell';
import useIsDesktop from './useIsDesktop';
import '../../components/harbour/harbourDocuments.css';
import './familyDocuments.css';

/*
 * Family portal · Add a document (TDI-579). Phone first: D-06 Q3, the form
 * with the notice under it and Save in the sticky bar, Back in the app bar.
 * Desktop: D-05 P6, P7 and P11 to P14, a wide task flyout over Your
 * children with Cancel and the gold Save. Saving goes back to where the
 * person came from with "... has been added."
 */
export default function FamilyAddDocumentPage() {
  const navigate = useNavigate();
  const { patientRecordId } = useParams();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  const { pupils, loading, error, reload } = useSencoPupils({ orgId, claimsOrgId });
  const isDesktop = useIsDesktop();
  const base = `/rms/family/child/${patientRecordId}`;

  useEffect(() => {
    if (claims && claims.userType && claims.userType !== 'RMS Legal Guardian') {
      navigate('/rms/dashboard', { replace: true });
    }
  }, [claims, navigate]);

  const pupil = useMemo(() => pupils.find((p) => p.id === patientRecordId) || null, [pupils, patientRecordId]);
  const child = useMemo(() => buildFamilyChild(pupil), [pupil]);
  const documentsOn = Boolean(child && child.referral && !child.referral.stopped);
  const docs = useMyReferralDocuments({ orgId, patientRecordId, enabled: documentsOn });

  const userName = familyUserName(user, pupils);
  // Desktop came from the record flyout; a phone from the Documents screen.
  const returnTo = isDesktop ? base : `${base}/documents`;
  const back = { to: returnTo, label: 'Back' };
  const goBack = () => navigate(returnTo, { state: { stay: true } });
  const onAdded = (doc) => navigate(returnTo, {
    state: { stay: true, documentNotice: addedNotice(doc && doc.description) },
  });

  if (loading) {
    return <FamilyPortalShell userName={userName} back={back}><FamilyLoadingState /></FamilyPortalShell>;
  }
  if (error || !pupil) {
    return <FamilyPortalShell userName={userName} back={back}><FamilyLoadError onRetry={() => reload()} /></FamilyPortalShell>;
  }

  const body = !documentsOn || (docs.summary && docs.summary.onReferralPathway === false) ? (
    <HarbourNotice kind="info" title="Documents can only be added to a referral" />
  ) : !docs.summary && !docs.error ? (
    <div aria-hidden="true" className="flex flex-col gap-5 max-w-[760px]">
      <div className="harbour-skeleton h-[108px] animate-pulse" />
      <div className="harbour-skeleton h-[80px] animate-pulse" />
    </div>
  ) : docs.error && !docs.summary ? (
    <HarbourNotice kind="error" title="We could not open this form">
      <p>{DOCUMENTS_LOAD_FAILED}</p>
      <HarbourTextLink onClick={() => docs.reload()}>Try again</HarbourTextLink>
    </HarbourNotice>
  ) : (
    <HarbourDocumentForm
      orgId={orgId}
      patientRecordId={patientRecordId}
      summary={docs.summary}
      tone="family"
      layout={isDesktop ? 'desktop' : 'phone'}
      onAdded={onAdded}
      onCancel={isDesktop ? goBack : undefined}
    />
  );

  if (isDesktop) {
    const allChildren = (pupils || []).map(buildFamilyChild).filter(Boolean);
    return (
      <FamilyPortalShell userName={userName} maxWidth="full">
        <div className="family-page-head">
          <h1 className="family-page-head__title family-page-head__title--home">Your children</h1>
          <p className="family-page-head__intro">Choose your child’s name to see their details and questions.</p>
        </div>
        <ul className="family-child-cards">
          {allChildren.map((c) => (
            <li key={c.id}><FamilyChildCard child={c} linkState={{ stay: true }} /></li>
          ))}
        </ul>
        <HarbourFlyout
          open
          width="wide"
          title={pupil.name}
          year={pupil.yearGroup}
          eyebrow={{ tone: 'task', label: 'Add a document' }}
          onClose={goBack}
        >
          {body}
        </HarbourFlyout>
      </FamilyPortalShell>
    );
  }

  return (
    <FamilyPortalShell userName={userName} back={back}>
      <div className="family-docs">
        <header className="flex flex-col gap-2">
          <p className="family-docs__eyebrow">
            <HarbourPathwayMark pathway="referral" />
            <span>{`Referral pathway · ${pupil.name}`}</span>
          </p>
          <h1 className="family-docs__title">Add a document</h1>
        </header>
        {body}
      </div>
    </FamilyPortalShell>
  );
}
